import React, { useCallback, useEffect, useLayoutEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import useColors from 'hooks/useColors';
import useColorScheme from 'hooks/useColorScheme';
import { useAppSelector } from 'store/hooks';
import { AppView, CardView, IconEdit, rem, SettingsItem, SettingsItemNavigate, SettingsItemRightContent, Spacer, TextButton, } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Section from 'design-system/Section';
import { IconGlobeNew } from 'design-system/icons';
import { IconInstagram, IconX } from 'design-system/icons/SocialMedia';
import { isWeb } from '../../../../constants';
import { makeSelectTransactionById, selectHasSuggestInstagramMerchantFeature, } from '../../selectors';
import TransactionLogo from '../../components/TransactionLogo';
import { normalizeHandleForDisplay } from './util';
const strings = {
    titleAdd: 'Add custom logo',
    titleEdit: 'Edit custom logo',
    heading: 'Edit a logo using the merchant’s social media or website. Just provide their username or URL',
    options: {
        TWITTER: 'X profile',
        INSTAGRAM: 'Instagram profile',
        WEB_DOMAIN: 'Website',
    },
    sectionHeadingCurrent: 'Current edit',
    sectionHeadingOther: 'Other options',
    deleteButton: 'Delete',
};
const CurrentSuggestedItem = React.memo(({ data, onPress, disabled }) => {
    const description = normalizeHandleForDisplay(data);
    const merchant = { displayName: data.name, iconUrl: data.iconUrl };
    return (<SettingsItem renderIcon={() => (<>
          <TransactionLogo R44 transaction={{ merchant }}/>
          <Spacer.W16 />
        </>)} title={strings.options[data.type]} description={description} onPress={onPress} disabled={disabled}>
      <SettingsItemRightContent>
        <IconEdit colorKey={'secondary'}/>
      </SettingsItemRightContent>
    </SettingsItem>);
});
const SuggestMerchantOptionsView = ({ suggested, onPress, deleteSuggestion, }) => {
    const hasSuggestInstagramMerchantFeature = useAppSelector(selectHasSuggestInstagramMerchantFeature);
    const colorScheme = useColorScheme();
    return (<View>
      <ScreenInfoHeader subTitle={strings.heading} style={styles.heading}/>
      {suggested ? (<Section title={strings.sectionHeadingCurrent} titleContainerStyle={styles.marginTop} button={<TextButton text={strings.deleteButton} onPress={deleteSuggestion}/>}>
          <CardView noMarginTop noMarginBottom webPadding={isWeb}>
            <CurrentSuggestedItem onPress={onPress(suggested.type)} data={suggested} disabled={!hasSuggestInstagramMerchantFeature &&
                suggested.type === 'INSTAGRAM'}/>
          </CardView>
        </Section>) : null}
      <Section title={suggested ? strings.sectionHeadingOther : undefined} titleContainerStyle={styles.marginTop}>
        <CardView noMarginTop webPadding={isWeb}>
          {suggested?.type !== 'TWITTER' && (<SettingsItemNavigate renderIcon={() => (<>
                  <IconX white={colorScheme === 'dark'}/>
                  <Spacer.W16 />
                </>)} title={strings.options.TWITTER} onPress={onPress('TWITTER')}/>)}
          {hasSuggestInstagramMerchantFeature &&
            suggested?.type !== 'INSTAGRAM' ? (<SettingsItemNavigate renderIcon={() => (<>
                  <IconInstagram />
                  <Spacer.W16 />
                </>)} title={strings.options.INSTAGRAM} onPress={onPress('INSTAGRAM')}/>) : null}
          {suggested?.type !== 'WEB_DOMAIN' && (<SettingsItemNavigate iconSize={44} icon={<IconGlobeNew gradientKey={'skyBlue'}/>} title={strings.options.WEB_DOMAIN} onPress={onPress('WEB_DOMAIN')}/>)}
        </CardView>
      </Section>
    </View>);
};
const SuggestMerchantOptionsScreen = ({ navigation, route }) => {
    const colors = useColors();
    const transactionId = route.params?.transactionId;
    const selectTransactionById = useMemo(makeSelectTransactionById, []);
    const transaction = useAppSelector((store) => selectTransactionById(store, transactionId));
    const currentSuggestion = transaction?.suggestedData ?? null;
    useLayoutEffect(() => {
        navigation.setOptions({
            title: currentSuggestion ? strings.titleEdit : strings.titleAdd,
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, currentSuggestion]);
    const onPress = useCallback((option) => () => {
        navigation.navigate('MerchantFeedback', {
            keyBack: route.key,
            transactionId,
            suggestType: option,
        });
    }, [route.key, transactionId]);
    const goToDeletePreview = useCallback(() => {
        navigation.navigate('PreviewMerchantOverride', {
            keyBack: route.key,
            transactionId,
            merchantData: null,
        });
    }, [route.key, transactionId]);
    useEffect(() => {
        if (!transaction) {
            if (navigation.canGoBack()) {
                navigation.goBack();
            }
            else {
                navigation.navigate('Feed');
            }
        }
    }, [transaction]);
    if (!transaction) {
        return null;
    }
    return (<AppView avoidHeader>
        <SuggestMerchantOptionsView suggested={currentSuggestion} onPress={onPress} deleteSuggestion={goToDeletePreview}/>
      </AppView>);
};
const styles = StyleSheet.create({
    heading: {
        marginBottom: rem(16),
        paddingBottom: 0,
    },
    marginTop: {
        marginTop: rem(8),
    },
});
export default SuggestMerchantOptionsScreen;
